import { Check, Shield, Users } from 'lucide-react';

export interface PlayerForm {
  licenseNumber: string;
  lastName: string;
  firstName: string;
  /** Auto-déclaration sur l'honneur (concours réservés aux licenciés) */
  licenseAutoDeclared?: boolean;
}

interface PlayerFieldsProps {
  index: number;
  label: string;
  value: PlayerForm;
  onChange: (v: PlayerForm) => void;
  accentColor?: 'blue' | 'emerald' | 'purple';
  licenseRequired?: boolean;
  /** Licence déjà utilisée par un autre joueur de l'équipe */
  duplicateLicense?: boolean;
}

export default function PlayerFields({
  index,
  label,
  value,
  onChange,
  accentColor = 'blue',
  licenseRequired = true,
  duplicateLicense = false,
}: PlayerFieldsProps) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
  };
  const inputCls =
    'w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all';

  return (
    <div className='bg-white rounded-3xl border border-slate-100 shadow-sm p-8'>
      <div className='flex items-center gap-3 mb-6'>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[accentColor]}`}>
          {index === 1 ? <Shield className='w-5 h-5' /> : <Users className='w-5 h-5' />}
        </div>
        <div>
          <h2 className='text-xl font-extrabold text-slate-900'>{label}</h2>
          {index === 1 && (
            <p className='text-sm text-slate-500'>Responsable de l&apos;équipe et interlocuteur principal</p>
          )}
        </div>
      </div>
      <div className='space-y-5'>
        {licenseRequired && (
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-2'>
              N° de licence FFPJP <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={value.licenseNumber}
              onChange={e => onChange({ ...value, licenseNumber: e.target.value })}
              placeholder='Ex : 069 12345'
              className={inputCls}
              required
              maxLength={30}
            />
            {duplicateLicense ? (
              <p className='text-xs text-red-600 mt-1 font-medium'>
                Un même numéro de licence ne peut pas être utilisé deux fois dans la même équipe.
              </p>
            ) : (
              <p className='text-xs text-slate-400 mt-1'>
                Numéro renseigné par le joueur. BoulesConnect ne vérifie pas automatiquement la validité de la licence.
              </p>
            )}

            {/* Auto-déclaration sur l'honneur */}
            <label className='mt-3 flex items-start gap-3 cursor-pointer group'>
              <button
                type='button'
                onClick={() => onChange({ ...value, licenseAutoDeclared: !value.licenseAutoDeclared })}
                className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                  value.licenseAutoDeclared
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-slate-300 group-hover:border-blue-400'
                }`}
                aria-pressed={!!value.licenseAutoDeclared}
              >
                {value.licenseAutoDeclared && <Check className='w-3 h-3 text-white' />}
              </button>
              <span className='text-xs text-slate-600'>
                Je déclare sur l&apos;honneur que ce joueur est titulaire de cette licence FFPJP, qu&apos;elle est
                valide pour la saison en cours et qu&apos;elle n&apos;est pas sous le coup d&apos;une suspension.
              </span>
            </label>
          </div>
        )}
        <div className='grid md:grid-cols-2 gap-5'>
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-2'>
              Nom <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={value.lastName}
              onChange={e => onChange({ ...value, lastName: e.target.value })}
              placeholder='DUPONT'
              className={inputCls}
              required
            />
          </div>
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-2'>
              Prénom <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              value={value.firstName}
              onChange={e => onChange({ ...value, firstName: e.target.value })}
              placeholder='Jean'
              className={inputCls}
              required
            />
          </div>
        </div>
      </div>
    </div>
  );
}
